// Applies the per-route chrome upstream declared as ui-router state `data` (§1.3).
//
// Upstream's router hooks read `data.bodyClass` and `data.pageTitle` off the state being
// entered and set them on the document. That is not cosmetic: `no-min-width` cancels
// `body { min-width: 1200px }` from the main app's theme, and without it every extension
// screen renders 1200px wide inside a 200-378px iframe. The ported login screen overflowed
// horizontally until this landed — visible in the pixel diff, invisible in a unit test.
//
// The values come from packages/protocol/routes.json rather than being retyped here, which
// is the whole point of extracting `data` in Phase 0.2: the route table is the source, so a
// bodyClass that changes upstream shows up as a regenerated artefact instead of as a screen
// that quietly starts rendering at the wrong width.
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { inheritedString } from './routeData.ts'

function chromeFor(pathname: string): { bodyClass?: string; pageTitle?: string } {
  // ⚠️ PER KEY. This used to take the longest matching route and read both values off it,
  // which is right whenever the leaf declares them and silently wrong whenever an ancestor
  // does — `/extension/shortlist` declares `bodyClass: 'no-min-width'` and
  // `/extension/shortlist/review` declares neither, so the leaf won and no class was applied.
  // The extension chrome rendered 1190px wide inside a 378px frame. ui-router merges `data`
  // down the state chain; `inheritedData` does the same against the flat table.
  return {
    bodyClass: inheritedString(pathname, 'bodyClass'),
    pageTitle: inheritedString(pathname, 'pageTitle'),
  }
}

export function useRouteChrome(): void {
  const { pathname } = useLocation()

  useEffect(() => {
    const { bodyClass, pageTitle } = chromeFor(pathname)
    if (bodyClass === undefined) return undefined

    // Upstream replaces the state-specific class on each transition and leaves the static
    // ones (`chrome-extension`, the env mode) alone, so only what this hook added is removed.
    const added = bodyClass.split(/\s+/).filter((name) => name.length > 0)
    document.body.classList.add(...added)
    const previousTitle = document.title
    if (pageTitle !== undefined) document.title = pageTitle

    return () => {
      document.body.classList.remove(...added)
      document.title = previousTitle
    }
  }, [pathname])
}
